import { Injectable, inject } from '@angular/core';
import { UIConstants } from '../constants/UI.constants';
import { ConciergeObligationTypes } from '../models/concierge-obligation.types';
import { MarketingOrder } from '../models/marketing-order.model';
import { ProductInstance, ProductInstanceApproval } from '../models/product-instance.model';
import { ServiceActions, ServiceStatusType } from '../models/service-status.type';
import { MarketingOrderProductService } from './marketing-order-product.service';
import { PromptDialogService } from './prompt-dialog.service';
import { ToasterService } from './toaster.service';
import { ProductCategory, ProductCode } from '../models';

@Injectable({
  providedIn: 'root',
})
export class ProductStateService {
  private productService = inject(MarketingOrderProductService);
  private toasterService = inject(ToasterService);
  private dialogService = inject(PromptDialogService);
  private readonly inFlightActions = new Set<string>();

  async completeProduct(marketingOrder: MarketingOrder, product: ProductInstance) : Promise<void | boolean> {
    if (product.category === ProductCategory.PRINT && !await this.validateProduct(marketingOrder, product)) {
      return;
    }

    if (product.code === ProductCode.VIDEO_SLIDESHOW) {
      const message = 'By completing this video product, you initiate its publication process. Ensure compliance with your region’s MLS regulations. Are you sure you want to proceed?';
      const response = await this.dialogService.openPrompt('Completion Confirmation', message, UIConstants.CONFIRM, [UIConstants.CANCEL]);
      if (response?.text !== UIConstants.CONFIRM) {
        return; // They did not confirm. Exit
      }
    }

    const onActionError = (error: any, action: ServiceActions) => {
      console.error(`[ProductStateService]: An error occurred performing product action ${action} on product`, { error, product });
      this.toasterService.showError(error?.message || 'An error occurred marking product as complete. Please check your task list.');
    };

    const onUpdated = (message?: string) => {
      this.toasterService.showInfo(message ?? 'Product has been updated!');
      return true;
    };

    if (product.status === ServiceStatusType.TODO) {
      if ([ConciergeObligationTypes.PARTIAL, ConciergeObligationTypes.NONE].includes(product.details.conciergeObligation) && product.isVendorPrinted) {
        return await this.productService.productAction(marketingOrder._id, ServiceActions.START, product)
          .then(() => onUpdated())
          .catch((error: Error) => onActionError(error, ServiceActions.START));
      }
    }

    if (product.status === ServiceStatusType.CREATING) {
      if ([ConciergeObligationTypes.PARTIAL, ConciergeObligationTypes.NONE].includes(product.details.conciergeObligation) && product.isVendorPrinted) {
        return await this.productService.productAction(marketingOrder._id, ServiceActions.PARTIALREVIEW, product)
          .then(() => onUpdated())
          .catch((error: Error) => onActionError(error, ServiceActions.PARTIALREVIEW));
      }
    }

    if (product.details.conciergeObligation === ConciergeObligationTypes.NONE || !product.isVendorPrinted) {
      return await this.productService.productAction(marketingOrder._id, ServiceActions.DELIVER, product)
        .then(() => onUpdated('Product has been completed!'))
        .catch((error: Error) => onActionError(error, ServiceActions.DELIVER));
    }
  }

  async sendForApproval(marketingOrder: MarketingOrder, product: ProductInstance) {
    if (!(product && marketingOrder)) { return; }

    // Validate the product first
    const isValid = await this.validateProduct(marketingOrder, product);
    if (!isValid) { return; }

    await this.runSingleFlight(marketingOrder, product, ServiceActions.REVIEW, async () => {
      await this.productService.productAction(marketingOrder._id, ServiceActions.REVIEW, product)
        .then(() => this.toasterService.showInfo('Product has been sent for approval!'))
        .catch((error: Error) => {
          console.error(`[ProductStateService]: An error occurred performing product action ${ServiceActions.REVIEW} on product`, { error, product, marketingOrder });
          this.toasterService.showError('An error occurred sending product for approval. Please check your task list.');
        });
    });
  }

  async approve(
    marketingOrder: MarketingOrder,
    product: ProductInstance,
    onError?: (message: string) => void,
  ): Promise<void | boolean> {
    if (!(product && marketingOrder)) { return; }

    // Validate the product and then update if it is valid
    const isValid = await this.validateProduct(marketingOrder, product);
    if (!isValid) return;

    product.approval = new ProductInstanceApproval({ approvedByAgent: true });
    return await this.runSingleFlight(marketingOrder, product, ServiceActions.APPROVE, async () => {
      return await this.productService.productAction(marketingOrder._id, ServiceActions.APPROVE, product)
        .then(() => {
          this.toasterService.showInfo('Product has been approved!');
          return true;
        })
        .catch((error: Error) => (onError ? onError(error.message) : this.toasterService.showError(error.message)));
    });
  }

  async reject(marketingOrder: MarketingOrder, product: ProductInstance, reason: string, notes: string) {
    product.approval = new ProductInstanceApproval({ approvedByAgent: false, reason, notes });
    await this.runSingleFlight(marketingOrder, product, ServiceActions.REJECT, async () => {
      await this.productService.productAction(marketingOrder._id, ServiceActions.REJECT, product)
        .then(() => this.toasterService.showInfo('Proof has been requested for revision'))
        .catch(() => this.toasterService.showError('An error occurred rejecting the proof'));
    });
  }

  /**
   * Validates the product before a state-transition.
   * NOTE: This is a UI only validation and will need to be implemented on the backend, but for the scope of LC-3966
   * we are only doing front-end validation
   * */
  async validateProduct(order: MarketingOrder, product: ProductInstance, textFits = true) {
    const bodyInfo = product.isVideoService()
      ? product.details?.bodyLayouts
      : product.selectedTemplate?.templateInfo?.bodyInfo;
    const headlineInfo = product.isVideoService() ? undefined : product.selectedTemplate?.templateInfo?.headlineInfo;
    const isValid = this.validateMaxChars(product, order, bodyInfo, 'marketingCopyBody')
      && this.validateMaxChars(product, order, headlineInfo, 'marketingCopyHeadline')
      && textFits;

    if (!isValid) {
      if (product.category === ProductCategory.PRINT) {
        await this.dialogService.openPrompt(
          'Character limit exceeded',
          'The text you provided is too long for this print product. Please shorten the text to continue.',
          'Close',
          [],
          { width: '600px', compact: true },
        );
      } else {
        await this.dialogService.openPrompt('Invalid', 'The product is invalid and cannot be submitted', 'Ok');
      }
    }
    return isValid;
  }

  /**
   * product - Selected product e.g. Video / Advertise / etc.
   * order - Specific marketing order
   * info - The values to select from product for comparison
   * array - The array containing the maxChar values for comparison
   * Purpose: Returns true or false if inputs do not exceed the max limit for a specified product.
   */
  validateMaxChars(
    product: ProductInstance,
    order: MarketingOrder,
    info: { maxChars?: number }[] | undefined,
    array: 'marketingCopyBody' | 'marketingCopyHeadline',
  ) {
    // Make sure the body layout is valid. If no body layouts exist, the function will .every(...) function will return true
    return (info || [])?.every((bodyInfo, index) => {
      if (!bodyInfo.maxChars) return true;
      const tag = `${array === 'marketingCopyBody' ? 'body' : 'headline'}[${index + 1}]`;
      if (product.category === ProductCategory.PRINT && product.hiddenTags?.includes(tag)) return true;
      // Get the body from the product. If not on the product, default it from the order
      const body = (product[array] || [])[index] || (order[array] || [])[index];
      if (product.category !== ProductCategory.PRINT) return (body || '').length <= bodyInfo.maxChars;

      const text = document.createElement('div');
      text.innerHTML = body || '';
      return text.textContent.length <= bodyInfo.maxChars;
    });
  }

  private async runSingleFlight<T>(
    marketingOrder: MarketingOrder,
    product: ProductInstance,
    action: ServiceActions,
    callback: () => Promise<T>,
  ): Promise<T | undefined> {
    const key = `${marketingOrder._id}:${product.code}:${action}`;
    if (this.inFlightActions.has(key)) {
      return undefined;
    }

    this.inFlightActions.add(key);
    try {
      return await callback();
    } finally {
      this.inFlightActions.delete(key);
    }
  }
}
